import React, {
  useState,
  useEffect,
  useCallback,
  FunctionComponent,
} from "react";
import isEmpty from "lodash.isempty";
import { AppState } from "@store";
import { connect } from "react-redux";
import { Grid, Box, Container } from "@mui/material";

import {
  setSmartCard,
  getSmartCards,
  setCouponStatus,
  generateJWTSession,
  resetSmartCardStatus,
} from "@ducks/profile";
import { ISmartCardsStatus, SmartCardStatus } from "@ducks/profile/types";
import { isIntegrationNeeded, typeOfIntegration } from "@lib/common";
import Breadcrumbs, { IBreadcrumb } from "@components/Breadcrumbs";
import { IItem, INTEGRATION_TYPE } from "@ducks/listing/types";
import { ILanguage } from "@ducks/config/types";
import { PORTAL } from "@ducks/config";

import Dropdown, { IOption } from "@components/Dropdown";

interface IStateProps {
  smartCards: string[];
  details: IItem | null;
  selectedSmartCard: string;
  smartCardsStatus: ISmartCardsStatus;
  language: ILanguage;
}

interface IOverheadProps extends IStateProps {
  getSmartCards: () => void;
  setSmartCard: (smartcard: string) => void;
  generateJWTSession: (smartcard: string) => void;
  setCouponStatus: () => void;
  resetSmartCardStatus: () => void;
  breadcrumbs?: IBreadcrumb[];
  showSmartCardSelector?: boolean;
}

const Overhead: FunctionComponent<IOverheadProps> = (props) => {
  const {
    selectedSmartCard,
    details,
    smartCards,
    smartCardsStatus,
    setSmartCard,
    generateJWTSession,
    setCouponStatus,
    resetSmartCardStatus,
    breadcrumbs,
    showSmartCardSelector,
    language,
  } = props;

  const [options, setOptions] = useState<IOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<IOption>({
    label: "",
    value: "",
  });
  const [smartCardBuffer, setSmartCardBuffer] = useState<string | null>(null);

  // Update coupon status from selected smart card
  useEffect(() => {
    if (selectedSmartCard !== "" && !isEmpty(smartCardsStatus)) {
      setCouponStatus();
      // if integration is iqiyi set smartcard to be the first in the list
      if (
        typeOfIntegration(details) === INTEGRATION_TYPE.iqiyi &&
        smartCards.length > 1 &&
        smartCardBuffer === null
      ) {
        setSmartCardBuffer(selectedSmartCard);
        setSmartCard(smartCards[0]);
      }
    }
  }, [
    selectedSmartCard,
    smartCardBuffer,
    setSmartCardBuffer,
    smartCardsStatus,
    setCouponStatus,
    details,
    smartCards,
    setSmartCard,
  ]);

  // Generate dropdown options
  useEffect(() => {
    if (!isEmpty(smartCardsStatus)) {
      const optionsTemp = [] as IOption[];
      const is3rdParty = isIntegrationNeeded(details);
      Object.keys(smartCardsStatus).map((smartCard) => {
        const value = smartCardsStatus[smartCard];
        let label = `Smartcard *** ${smartCard.substr(smartCard.length - 4)}`;

        if (
          value.reason === SmartCardStatus.redeemed &&
          (!details?.showTimer || !value.remainingTimeInMilli || is3rdParty)
        ) {
          label = `${label} (redeemed)`;
        }

        optionsTemp.push({ value: smartCard, label });

        if (smartCard === selectedSmartCard) {
          setSelectedOption({ value: smartCard, label });
          generateJWTSession(selectedSmartCard);
        }
      });
      setOptions(optionsTemp);
    } else if (smartCards.length > 0) {
      const optionsTemp = [] as IOption[];
      smartCards.map((smartCard) => {
        const label = `Smartcard *** ${smartCard.substr(smartCard.length - 4)}`;
        optionsTemp.push({ value: smartCard, label });
        if (smartCard === selectedSmartCard) {
          setSelectedOption({ value: smartCard, label });
          generateJWTSession(selectedSmartCard);
        }
      });

      setOptions(optionsTemp);
    } else {
      setOptions([]);
    }
  }, [
    details,
    smartCardsStatus,
    selectedSmartCard,
    generateJWTSession,
    setOptions,
    smartCards,
  ]);

  // Map individual smc status to coupon state when users change smartcard
  const handleChange = useCallback(
    (value: string) => {
      const selected = options.find((option) => option.value === value);
      if (selected) {
        setSelectedOption(selected);
        setSmartCard(value);
      }
    },
    [selectedOption, options, setSmartCard]
  );

  // Reset smartcard status on unmount
  useEffect(() => {
    return (): void => {
      resetSmartCardStatus();
    };
  }, [resetSmartCardStatus]);

  // Pass back buffered smart card if available on unmount (this happens on iqiyi integration)
  useEffect(() => {
    return (): void => {
      if (smartCardBuffer !== null) {
        setSmartCard(smartCardBuffer);
      }
    };
  }, [setSmartCard, smartCardBuffer]);

  return (
    <Container>
      <Grid container spacing={0}>
        <Grid item xl={12} lg={12} md={12} xs={12}>
          <Box
            sx={({ breakpoints }) => ({
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              [breakpoints.down("sm")]: {
                flexDirection: "column",
              },
            })}
          >
            {PORTAL === "promotions" && (
              <Box
                sx={({ breakpoints }) => ({
                  overflow: "hidden",
                  alignSelf: "center",
                  [breakpoints.down("sm")]: {
                    alignSelf: "flex-start",
                    width: "100%",
                    p: "0px",
                  },
                })}
              >
                <Breadcrumbs items={breadcrumbs || []} language={language} />
              </Box>
            )}

            {options.length > 1 && showSmartCardSelector && (
              <Box
                sx={({ breakpoints }) => ({
                  height: "100%",
                  minWidth: "272px",
                  alignSelf: "center",
                  [breakpoints.down("sm")]: {
                    mt: 2,
                    width: "100%",
                  },
                })}
              >
                <Dropdown
                  options={options}
                  initial={selectedOption}
                  onChange={handleChange}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

Overhead.displayName = "Overhead";

Overhead.defaultProps = {
  selectedSmartCard: "",
};

const mapDispatchToProps = {
  getSmartCards,
  setSmartCard,
  generateJWTSession,
  setCouponStatus,
  resetSmartCardStatus,
};

const mapStateToProps = (state: AppState): IStateProps => ({
  smartCards: state.profile.smartCards,
  selectedSmartCard: state.profile.selectedSmartCard,
  details: state.listing.details,
  smartCardsStatus: state.profile.smartCardsStatus,
  language: state.config.language,
});

export default connect(mapStateToProps, mapDispatchToProps)(Overhead);
